import React, { useCallback, useEffect, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";

import Container from "@material-ui/core/Container";
import { useDispatch, useSelector } from "react-redux";
import { logOut } from "../../auth/authActions";

import { find, join, map, mergeRight, propEq, props, reject } from "ramda";
import { fetchSecurityUser } from "../../util/fetchUtils";
import { isBlank, notBlank } from "../../util/ext";
import PanelLoadingIndicator from "../../components/PanelLoadingIndicator";
import Button from "@material-ui/core/Button";
import AddIcon from "@material-ui/icons/Add";
import TotalsHeader from "../../creator-application/common/TotalsHeader";
import {
  useIpOverrideDelete,
  useIpOverrides,
  useRegions,
} from "./ipOverrideServices";
import IpOverrideList from "./IpOverrideList";
import RealTimeSearchBar from "../../creator-application/common/RealTimeSearchBar";
import EditIpOverride from "./EditIpOverride";

const useStyles = scrollVisible =>
  makeStyles(theme => ({
    root: {
      border: "3px white solid",
      backgroundColor: "white",
      borderRadius: "20px",
    },

    panel: {
      backgroundColor: "white",
      paddingLeft: theme.spacing(4),
      paddingRight: scrollVisible ? theme.spacing(0) : theme.spacing(4),
    },

    listContainer: {
      height: "calc(100vh - 350px)",
      overflow: "auto",
    },
  }));

const initialSearchState = {
  filter: null,
  order: "ipAddress",
  page: 0,
  limit: 9999,
};

const userCache = new Map();
const lookupUser = (auth, userId) => {
  if (!userCache.has(userId)) {
    userCache.set(userId, fetchSecurityUser(auth)(userId));
  }

  return userCache.get(userId);
};

const IpOverrideBrowse = () => {
  const [scrollVisible, setScrollVisible] = useState(true);
  const auth = useSelector(state => state.auth);
  const dispatch = useDispatch();
  const classes = useStyles(scrollVisible)();

  const [ipOverrides, setIpOverrides] = useState([]);
  const [regions, setRegions] = useState([]);
  const [selectedOverride, setSelectedOverride] = useState({});

  //-- Handle Search state
  const [state, setState] = useState(initialSearchState);

  const results = useIpOverrides(
    state.filter,
    state.order,
    state.page,
    state.limit,
  );

  const regionResults = useRegions();

  useEffect(() => {
    if (results.isError && results.error.status === 401) {
      dispatch(logOut());
    }
    if (regionResults.isError && regionResults.error.status === 401) {
      dispatch(logOut());
    }

    const fetchUsers = async override => {
      const createdByUser = await lookupUser(auth, override.createdBy);

      return mergeRight(override, {
        createdByUsername: createdByUser.username,
      });
    };

    if (
      results.isSuccess &&
      regionResults.isSuccess &&
      notBlank(results.data) &&
      notBlank(regionResults.data)
    ) {
      setRegions(
        map(
          region =>
            mergeRight(region, {
              label: join(
                ", ",
                reject(
                  isBlank,
                  props(["city", "county", "state", "country"], region),
                ),
              ),
            }),
          regionResults.data,
        ),
      );
      setScrollVisible(true);
      Promise.all(map(fetchUsers, results.data)).then(data => {
        const regionIpOverrides = map(override => {
          const region = find(
            propEq("regionId", override.regionId),
            regionResults.data,
          );
          return mergeRight(override, { region: region });
        }, data);
        setIpOverrides(regionIpOverrides);
      });
    }
  }, [
    dispatch,
    auth,
    results.data,
    results.isError,
    results.error,
    results.isSuccess,
    regionResults.data,
    regionResults.isError,
    regionResults.error,
    regionResults.isSuccess,
  ]);

  const [ipOverrideDialogOpen, setIpOverrideDialogOpen] = useState(false);

  const handleNewIpOverrideClicked = useCallback(() => {
    setIpOverrideDialogOpen(true);
  }, []);

  //-- Handle Search
  const [appliedQuery, setAppliedQuery] = useState("");
  const handleQueryChange = updatedQuery => {
    setAppliedQuery(updatedQuery);
    setState(prev =>
      mergeRight(prev, {
        filter: notBlank(updatedQuery) ? updatedQuery : "",
      }),
    );
  };

  const ipOverrideDelete = useIpOverrideDelete();
  const handleIpOverrideDeleteClick = async override => {
    await ipOverrideDelete.mutateAsync({
      resourceIds: { regionIpOverrideId: override.regionIpOverrideId },
    });
    await results.refetch();
  };

  const handleIpOverrideEditClose = useCallback(() => {
    setIpOverrideDialogOpen(false);
  }, []);

  const handleIpOverrideEditSave = useCallback(
    async override => {
      console.log(override);
      setIpOverrideDialogOpen(false);
      await results.refetch();
      setSelectedOverride({ ipAddressAsString: override.ipAddress });
    },
    [results],
  );
  //-- Track if the scroll bar is visible
  const tableContainerEl = useRef(null);
  useEffect(() => {
    if (notBlank(tableContainerEl) && notBlank(tableContainerEl.current)) {
      const element = tableContainerEl.current;
      const scrollVisible = element.scrollHeight > element.clientHeight;
      console.log(`Scroll Visible : ${scrollVisible}`);
      setScrollVisible(scrollVisible);
    }
  }, [tableContainerEl]);

  return (
    <Container id="ip-overrides-browse-view" classes={{ root: classes.root }}>
      <EditIpOverride
        regions={regions}
        open={ipOverrideDialogOpen}
        onClose={handleIpOverrideEditClose}
        onSave={handleIpOverrideEditSave}
      />

      <div>
        <RealTimeSearchBar
          query={appliedQuery}
          onQueryChange={handleQueryChange}
        />

        <TotalsHeader
          countText="IP override"
          count={results.isSuccess ? ipOverrides.length : "-"}
          selectedActions={
            <div>
              <Button
                variant={"text"}
                startIcon={<AddIcon />}
                onClick={handleNewIpOverrideClicked}
              >
                New IP Override
              </Button>
            </div>
          }
        />

        {results.isLoading && <PanelLoadingIndicator />}
        {results.isSuccess && notBlank(ipOverrides) && (
          <Container classes={{ root: classes.panel }}>
            <Container ref={tableContainerEl} className={classes.listContainer}>
              <IpOverrideList
                overrides={ipOverrides}
                selected={selectedOverride}
                onDeleteItemClick={handleIpOverrideDeleteClick}
              />
            </Container>
          </Container>
        )}
      </div>
    </Container>
  );
};

export default IpOverrideBrowse;
